import React, { useState } from 'react';
import { QBox, QEmptyState, QInput, QStack } from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { WorkflowCard } from './WorkflowCard';
import Loading from '../shared/Loading/Loading';
import { CCWorkflowLink, isArchivedWorkflow } from '../../types/ccWorkflow';

const getSortRank = (wf: CCWorkflowLink): number => {
  if (wf.qualio_managed) return 0;
  if (isArchivedWorkflow(wf)) return 2;
  return 1;
};

type ChangeControlSettingsTabProps = {
  isLoading: boolean;
  selectedWorkflows: CCWorkflowLink[];
  workflows: CCWorkflowLink[];
  onChangeSelection: (checked: boolean, ccLink: CCWorkflowLink) => void;
};

export const ChangeControlSettingsTab: React.FC<ChangeControlSettingsTabProps> = ({
  isLoading,
  selectedWorkflows,
  workflows,
  onChangeSelection,
}) => {
  const [searchParams] = useSearchParams();
  const isEditing = searchParams.get('intent') === 'edit';
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  if (isLoading) return <Loading />;

  if (workflows.length === 0) {
    return (
      <QEmptyState
        title={t('template.ccSettings.emptyState.title')}
        subtitle={t('template.ccSettings.emptyState.subtitle')}
        data-cy="cc-settings-tab-empty-state"
        data-testid="cc-settings-tab-empty-state"
      />
    );
  }

  const sortedWorkflows = [...workflows].sort((a, b) => getSortRank(a) - getSortRank(b));

  const filteredWorkflows =
    isEditing && searchTerm
      ? sortedWorkflows.filter((wf) => wf.name.toLowerCase().includes(searchTerm.toLowerCase()))
      : sortedWorkflows;

  return (
    <QStack spacing={3} mt={4}>
      {isEditing && (
        <QBox width="75%">
          <QInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('template.ccSettings.searchPlaceholder')}
            data-cy="cc-workflow-search"
            data-testid="cc-workflow-search"
          />
        </QBox>
      )}
      {filteredWorkflows.length === 0 ? (
        <QEmptyState
          title={t('template.ccSettings.noResults.title')}
          subtitle={t('template.ccSettings.noResults.subtitle')}
          data-cy="cc-workflow-search-empty"
          data-testid="cc-workflow-search-empty"
        />
      ) : (
        filteredWorkflows.map((workflow) => (
          <WorkflowCard
            key={workflow.id}
            workflow={workflow}
            isEditing={isEditing}
            isSelected={selectedWorkflows.some((cc) => cc.id === workflow.id)}
            onSelect={onChangeSelection}
          />
        ))
      )}
    </QStack>
  );
};
